import { useState, useEffect, useRef, useCallback } from 'react';
import DashboardService from '../services/DashboardService';
import { useEmergency } from '../contexts/EmergencyContext';
import { getCurrentTenant } from '../services/FormsService';

/**
 * useMultiAlarmChecker
 * 여러 알람을 동시에 체크하고 관리하는 커스텀 훅
 *
 * 테넌트 변경 시 알람 설정을 자동으로 재로드하여
 * 이전 테넌트의 알람이 현재 테넌트에서 실행되는 문제를 방지합니다.
 */
const useMultiAlarmChecker = (enabled, globalVariables = null) => {
  const [alarmConfigs, setAlarmConfigs] = useState([]);
  const [activeAlarmKeys, setActiveAlarmKeys] = useState(new Set());
  const [activeAlarm, setActiveAlarm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentTenant, setCurrentTenant] = useState(() => getCurrentTenant());

  const timerRefsRef = useRef({});
  const isMountedRef = useRef(true);
  const alarmConfigsRef = useRef([]);
  const globalCooldownRef = useRef(0); // 글로벌 쿨다운 (즉시 반영 위해 useRef 사용)
  const snoozedAlarmsRef = useRef(new Map()); // Map<alarm_key, snoozedUntil> - 즉시 반영 위해 useRef 사용
  const lastTenantRef = useRef(currentTenant); // 테넌트 변경 감지용

  // 🔊 Emergency 사운드 컨텍스트 사용
  const { startEmergencySound, stopEmergencySound } = useEmergency();

  // 테넌트 변경 감지 (쿠키 폴링 방식)
  useEffect(() => {
    if (!enabled) return;

    const checkTenantChange = () => {
      const newTenant = getCurrentTenant();
      if (newTenant && newTenant !== lastTenantRef.current) {
        console.log(`[useMultiAlarmChecker] 테넌트 변경 감지: ${lastTenantRef.current} → ${newTenant}`);
        lastTenantRef.current = newTenant;
        setCurrentTenant(newTenant);

        // 테넌트 변경 시 기존 타이머 정리 및 상태 초기화
        Object.values(timerRefsRef.current).forEach(timer => {
          if (timer) clearInterval(timer);
        });
        timerRefsRef.current = {};
        setAlarmConfigs([]);
        setActiveAlarm(null);
        setActiveAlarmKeys(new Set());
        snoozedAlarmsRef.current.clear();
        globalCooldownRef.current = 0;

        // 사이렌 중지
        stopEmergencySound();
      }
    };

    // 1초마다 테넌트 변경 체크
    const intervalId = setInterval(checkTenantChange, 1000);

    return () => clearInterval(intervalId);
  }, [enabled, stopEmergencySound]);
  
  const loadAlarmConfigs = useCallback(async () => {
    if (!enabled) {
      setAlarmConfigs([]);
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('/api/visualization/alarms/', {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      const result = await response.json();

      if (result.success && Array.isArray(result.alarms)) {
        const enabledAlarms = result.alarms.filter(a => a.enabled);
        setAlarmConfigs(enabledAlarms);
        console.log(`[useMultiAlarmChecker] 알람 설정 로드 완료 (tenant: ${currentTenant}):`, enabledAlarms.length);
      } else {
        console.error('[useMultiAlarmChecker] 알람 목록 로드 실패:', result);
        setAlarmConfigs([]);
      }
    } catch (error) {
      console.error('[useMultiAlarmChecker] 알람 목록 로드 오류:', error);
      setAlarmConfigs([]);
    } finally {
      setLoading(false);
    }
  }, [enabled, currentTenant]); // currentTenant 추가: 테넌트 변경 시 알람 재로드
  
  const executeAlarmCondition = useCallback(async (alarmConfig) => {
    if (!alarmConfig || !alarmConfig.condition_sql) {
      return null;
    }
    
    try {
      const result = await DashboardService.getComponentData({
        data_source: {
          type: 'sql',
          query: alarmConfig.condition_sql
        },
        variables: globalVariables?.variables || {}
      });
      
      if (!result.success || !result.data || !result.data.data) {
        return null;
      }
      
      const firstRow = result.data.data[0];
      if (!firstRow) {
        return null;
      }
      
      const shouldShow = firstRow.should_show || firstRow.SHOULD_SHOW || 
                        firstRow.shouldShow || firstRow.shouldshow;
      const alarmKey = firstRow.alarm_key || firstRow.ALARM_KEY || 
                      firstRow.alarmKey || firstRow.alarmkey;
      
      const normalizedAlarmKey = alarmKey != null ? String(alarmKey) : null;
      
      return {
        should_show: Boolean(shouldShow),
        alarm_key: normalizedAlarmKey,
        alarm_config_id: alarmConfig.id
      };
    } catch (error) {
      console.error('[useMultiAlarmChecker] SQL 실행 중 오류:', error);
      return null;
    }
  }, [globalVariables]);
  
  const canShowAlarm = useCallback((alarmKey) => {
    if (!alarmKey) return false;
    if (activeAlarmKeys.has(alarmKey)) return false;
    
    // 글로벌 쿨다운 체크 (닫기 후 5초간 모든 알람 차단)
    const now = Date.now();
    const cooldownUntil = globalCooldownRef.current;
    if (cooldownUntil > 0 && now < cooldownUntil) {
      return false;
    }
    
    // alarm_key 기반 스누즈 체크 (각 인스턴스 독립적)
    const snoozedUntil = snoozedAlarmsRef.current.get(alarmKey);
    if (snoozedUntil) {
      if (now < snoozedUntil) {
        // 스누즈 중
        console.log(`[useMultiAlarmChecker] 스누즈 중: ${alarmKey}, ${Math.floor((snoozedUntil - now) / 1000)}초 남음`);
        return false;
      } else {
        // 스누즈 만료
        console.log(`[useMultiAlarmChecker] 스누즈 만료: ${alarmKey}`);
        snoozedAlarmsRef.current.delete(alarmKey);
      }
    }
    
    return true;
  }, [activeAlarmKeys]);
  
  const checkSingleAlarm = useCallback(async (alarmConfig) => {
    if (!isMountedRef.current || !alarmConfig.enabled) {
      // 알람 비활성화 시에만 로그 (반복 로그 방지)
      return;
    }
    
    try {
      // 디버깅: SQL 실행 중 로그는 주석 처리 (너무 많이 출력됨)
      // console.log(`[useMultiAlarmChecker] 알람 조건 SQL 실행 중: ${alarmConfig.alarm_name}`);
      const result = await executeAlarmCondition(alarmConfig);
      
      // 디버깅: SQL 결과 로그는 주석 처리 (너무 많이 출력됨)
      // console.log(`[useMultiAlarmChecker] SQL 결과 (${alarmConfig.alarm_name}):`, result);
      
      if (!result) {
        console.error(`[useMultiAlarmChecker] SQL 결과 없음: ${alarmConfig.alarm_name}`);
        return;
      }
      
      if (!result.should_show) {
        // should_show=false는 정상 상황이므로 로그 불필요
        return;
      }
      
      const { alarm_key } = result;
      
      // 이미 팝업이 표시 중이면 대기
      setActiveAlarm(currentActiveAlarm => {
        if (currentActiveAlarm) {
          // 디버깅: 대기 로그 주석 처리 (반복 출력 방지)
          // console.log(`[useMultiAlarmChecker] 알람 대기 중 (이미 팝업 표시 중): ${alarmConfig.alarm_name}`);
          return currentActiveAlarm; // 기존 팝업 유지
        }
        
        if (!canShowAlarm(alarm_key)) {
          // 스누즈 중일 때는 로그 불필요 (canShowAlarm에서 이미 출력)
          return null;
        }
        
        console.log(`[useMultiAlarmChecker] ✅ 알람 팝업 표시!`, {
          alarm_name: alarmConfig.alarm_name,
          alarm_key,
          dashboard_id: alarmConfig.alarm_dashboard_id
        });

        // 🔊 알람 팝업 표시 시 사이렌 소리 시작
        startEmergencySound();

        // activeAlarmKeys에 추가
        setActiveAlarmKeys(prev => {
          const next = new Set(prev);
          next.add(alarm_key);
          return next;
        });

        return {
          alarm_key,
          dashboard_id: alarmConfig.alarm_dashboard_id,
          snooze_duration: alarmConfig.snooze_duration || 300,
          popup_size_mode: alarmConfig.popup_size_mode || 'fixed',
          popup_width: alarmConfig.popup_width,
          popup_height: alarmConfig.popup_height,
          popup_padding_vertical: alarmConfig.popup_padding_vertical || 40,
          popup_padding_horizontal: alarmConfig.popup_padding_horizontal || 40,
          popup_transparent_mode: alarmConfig.popup_transparent_mode === true,  // 실제 값 사용
          on_confirm: {
            function_name: alarmConfig.on_confirm_function,
            parameters: alarmConfig.on_confirm_parameters
          }
        };
      });
    } catch (error) {
      console.error('[useMultiAlarmChecker] 알람 체크 중 오류:', error);
    }
  }, [executeAlarmCondition, canShowAlarm, startEmergencySound]);
  
  const handleAlarmConfirm = useCallback(async (alarmKey, popupVariables = {}) => {
    console.log('[useMultiAlarmChecker] 알람 확인 시작:', { alarmKey, activeAlarm, popupVariables });

    // 🔇 알람 확인 시 사이렌 중지
    stopEmergencySound();

    if (!activeAlarm || !activeAlarm.on_confirm) {
      console.log('[useMultiAlarmChecker] on_confirm 설정 없음, 팝업만 닫기');
      setActiveAlarm(null);
      setActiveAlarmKeys(prev => {
        const next = new Set(prev);
        next.delete(alarmKey);
        return next;
      });
      return;
    }
    
    try {
      const { function_name, parameters } = activeAlarm.on_confirm;
      
      console.log('[useMultiAlarmChecker] DB 함수 호출 준비:', { function_name, parameters });
      
      // 파라미터 변수 치환
      const processedParameters = {};
      for (const [key, value] of Object.entries(parameters || {})) {
        if (typeof value === 'string') {
          let processed = value
            .replace(/\$\{alarm_key\}/g, alarmKey)
            .replace(/\$\{current_user\}/g, 'current_user')
            .replace(/\$\{current_timestamp\}/g, new Date().toISOString());
          
          // 팝업 변수 치환: ${popup.note}, ${popup.reason} 등
          // 정규식으로 모든 ${popup.xxx} 패턴 찾기
          const popupVarPattern = /\$\{popup\.(\w+)\}/g;
          processed = processed.replace(popupVarPattern, (match, varName) => {
            const varValue = popupVariables[varName];
            if (varValue !== undefined && varValue !== null) {
              return String(varValue);
            }
            console.warn(`[useMultiAlarmChecker] 팝업 변수 '${varName}'를 찾을 수 없음, 원본 유지:`, match);
            return match; // 변수를 찾지 못하면 원본 유지
          });
          
          processedParameters[key] = processed;
        } else {
          processedParameters[key] = value;
        }
      }
      
      console.log('[useMultiAlarmChecker] 파라미터 치환 후:', processedParameters);
      
      const requestBody = {
        functionName: function_name,  // camelCase로 변경
        parameters: processedParameters
      };
      
      console.log('[useMultiAlarmChecker] 요청 body:', JSON.stringify(requestBody, null, 2));
      
      if (!function_name) {
        console.log('[useMultiAlarmChecker] function_name 없음, 팝업만 닫기');
        setActiveAlarm(null);
        setActiveAlarmKeys(prev => {
          const next = new Set(prev);
          next.delete(alarmKey);
          return next;
        });
        return;
      }
      
      console.log('[useMultiAlarmChecker] 최종 요청 body:', JSON.stringify(requestBody, null, 2));
      
      const response = await fetch('/api/dashboard/function/execute/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });
      
      const result = await response.json();
      
      if (!result.success) {
        const errorMsg = result.error || '알람 확인 처리 실패';
        alert(`⚠️ 알람 확인 실패\n\n${errorMsg}\n\n관리자에게 문의하세요.`);
        throw new Error(errorMsg);
      }
      
      console.log('[useMultiAlarmChecker] 알람 확인 완료:', alarmKey);
      
      if (isMountedRef.current) {
        setActiveAlarm(null);
        setActiveAlarmKeys(prev => {
          const next = new Set(prev);
          next.delete(alarmKey);
          return next;
        });
      }
    } catch (error) {
      console.error('[useMultiAlarmChecker] 알람 확인 처리 중 오류:', error);
      // JSON 파싱 실패 또는 네트워크 오류 시에도 사용자에게 알림
      if (error.message && !error.message.includes('알람 확인 처리 실패')) {
        alert(`⚠️ 알람 확인 중 오류 발생\n\n${error.message}\n\n관리자에게 문의하세요.`);
      }
      throw error;
    }
  }, [activeAlarm, stopEmergencySound]);

  const handleAlarmSnooze = useCallback((alarmKey, snoozeDuration) => {
    const now = Date.now();
    const snoozedUntil = now + (snoozeDuration * 1000);
    const globalCooldownUntil = now + 5000; // 5초간 모든 알람 차단

    console.log(`[useMultiAlarmChecker] 알람 스누즈: alarm_key=${alarmKey}, ${snoozeDuration}초 후 재알림 (${Math.floor(snoozeDuration/60)}분)`);

    // 🔇 스누즈(닫기) 시 사이렌 중지
    stopEmergencySound();

    if (isMountedRef.current) {
      // 글로벌 쿨다운 설정 (5초간 모든 알람 차단) - useRef로 즉시 반영
      globalCooldownRef.current = globalCooldownUntil;

      // alarm_key 기반으로 스누즈 저장 (개별 인스턴스 독립적) - useRef로 즉시 반영
      snoozedAlarmsRef.current.set(alarmKey, snoozedUntil);

      setActiveAlarm(null);
      setActiveAlarmKeys(prev => {
        const next = new Set(prev);
        next.delete(alarmKey);
        return next;
      });
    }
  }, [stopEmergencySound]);
  
  useEffect(() => {
    alarmConfigsRef.current = alarmConfigs;
  }, [alarmConfigs]);
  
  useEffect(() => {
    isMountedRef.current = true;
    if (enabled) {
      loadAlarmConfigs();
    }
    return () => {
      isMountedRef.current = false;
    };
  }, [enabled, loadAlarmConfigs, currentTenant]); // currentTenant 추가: 테넌트 변경 시 알람 재로드
  
  useEffect(() => {
    if (!enabled || alarmConfigs.length === 0) {
      Object.values(timerRefsRef.current).forEach(timer => {
        if (timer) clearInterval(timer);
      });
      timerRefsRef.current = {};
      return;
    }
    
    // 타이머 설정 (checkSingleAlarm을 인라인으로 호출)
    alarmConfigs.forEach(alarmConfig => {
      const alarmId = alarmConfig.id;
      
      if (timerRefsRef.current[alarmId]) {
        clearInterval(timerRefsRef.current[alarmId]);
      }
      
      // 즉시 첫 번째 체크 실행
      setTimeout(() => checkSingleAlarm(alarmConfig), 100);
      
      const checkInterval = (alarmConfig.check_interval || 2) * 1000;
      timerRefsRef.current[alarmId] = setInterval(() => {
        checkSingleAlarm(alarmConfig);
      }, checkInterval);
      
      // 디버깅: 알람 체크 시작 로그 주석 처리 (반복 출력 방지)
      // console.log(`[useMultiAlarmChecker] 알람 체크 시작: ${alarmConfig.alarm_name} (주기: ${alarmConfig.check_interval}초)`);
    });
    
    return () => {
      Object.values(timerRefsRef.current).forEach(timer => {
        if (timer) clearInterval(timer);
      });
      timerRefsRef.current = {};
    };
  }, [enabled, alarmConfigs, checkSingleAlarm]);
  
  return {
    alarmConfigs,
    activeAlarm,
    handleAlarmConfirm,
    handleAlarmSnooze,
    reloadAlarms: loadAlarmConfigs
  };
};

export default useMultiAlarmChecker;

